import type { Address } from "viem"
import type { Config } from "wagmi"
import { readContract } from "@wagmi/core"

/**
 * Minimal fragment of `IUniswapV3Pool` used to read `slot0()`. Every
 * Uniswap-v3 fork (including thick-v2 on Base) implements this identically,
 * so the ABI is bit-exact across deployments.
 *
 * We keep the ABI local to this lens to avoid dragging in the full pool ABI
 * for a read that only needs `sqrtPriceX96`, `tick`, and `observationIndex`.
 */
export const thickV2PoolSlot0Abi = [
  {
    inputs: [],
    name: "slot0",
    outputs: [
      { internalType: "uint160", name: "sqrtPriceX96", type: "uint160" },
      { internalType: "int24", name: "tick", type: "int24" },
      { internalType: "uint16", name: "observationIndex", type: "uint16" },
      { internalType: "uint16", name: "observationCardinality", type: "uint16" },
      { internalType: "uint16", name: "observationCardinalityNext", type: "uint16" },
      { internalType: "uint8", name: "feeProtocol", type: "uint8" },
      { internalType: "bool", name: "unlocked", type: "bool" },
    ],
    stateMutability: "view",
    type: "function",
  },
] as const

export type PoolSlot0Result = {
  sqrtPriceX96: bigint
  tick: number
  observationIndex: number
}

/**
 * Read `slot0()` from a thick-v2 pool. Single, narrowly-typed RPC call; larger
 * batched readers live in `transports/` and call this function per pool via
 * the chunked RPC strategy.
 */
export async function poolSlot0({
  poolAddress,
  chainId,
  config,
}: {
  poolAddress: Address
  chainId: number
  config: Config
}): Promise<PoolSlot0Result> {
  const slot0 = (await readContract(config, {
    abi: thickV2PoolSlot0Abi,
    address: poolAddress,
    chainId,
    functionName: "slot0",
  })) as unknown as readonly [bigint, number, number, number, number, number, boolean]

  return {
    sqrtPriceX96: slot0[0],
    tick: slot0[1],
    observationIndex: slot0[2],
  }
}
